import React, { useState, useRef, useEffect } from "react";
import "./tailwind.min.css";
import "./App.css";
import { Dark } from "./Theme";
import anime from "animejs";
import * as R from "rambda";
import {
  ThemeProvider,
  Stack,
  Input,
  Button,
  H1,
  P
} from "@wigxel/react-components";
import Poll from "./components/Poll";
import polls from "./libs/polls";
import { log, isEmail, trace } from "@wigxel/utils";
import Intro from "./components/Intro";
import scrollUp from "./images/line-top.svg";
import { voteForUser, emailExist, addVoterEmail } from "./libs/firebase";

function App() {
  const lastSection = useRef();
  const [voting, setVoting] = useState(true);
  const [answers, setAnswers] = useState([]);

  return (
    <div className="App">
      <ThemeProvider theme={Dark}>
        <React.Fragment>
          <footer
            className="absolute text-xs opacity-25 hover:opacity-75"
            style={{ bottom: 10, left: 10, zIndex: 3 }}
          >
            <a href="//wigxel.owonwo.now.sh">
              CRAFTED BY
              <br />
              <b className="text-lg">Wigxel</b> with ♥
            </a>
          </footer>

          {voting && (
            <VotingArea
              setAnswers={arr => {
                setAnswers(arr);
              }}
              doneVoting={userId => {
                const getName = ([key, obj]) => [polls[key].name, obj.answer];
                const addToDB = ([key, obj]) => voteForUser(key, obj, userId);
                const store = R.pipe(
                  getName,
                  addToDB
                );

                Promise.all(answers.map(store))
                  .then(trace("Updated firebase!"))
                  .then(() => {
                    setVoting(false);
                  });
              }}
            />
          )}
          {voting || (
            <section className="far-te" ref={lastSection}>
              <Animation type="slideInDown" duration={200}>
                <H1>THANKS FOR VOTING</H1>
              </Animation>
              <Animation type="slideInLeft" duration={900}>
                <P>
                  <b>We really appreciate your support</b>. - Team Mediaville
                </P>
              </Animation>
            </section>
          )}
        </React.Fragment>
      </ThemeProvider>
    </div>
  );
}

const Animation = ({ type, duration, children }) => {
  const [move, setMove] = useState("");
  useEffect(() => {
    setTimeout(() => setMove("move"), duration);
  });

  return <div className={["animate", type, move].join(" ")}>{children}</div>;
};

const voting = {
  ends: true,
}

const VotingArea = ({ doneVoting, setAnswers }) => {
  const pollArea = useRef();
  const introArea = useRef();
  const [state, setState] = useState({
    current: 0,
    userEmail: "",
    userId: "",
    emailVerified: false,
    answers: new Map([])
  });
  const setEmail = userEmail => setState({ ...state, userEmail });
  const setCurrent = current => setState({ ...state, current });
  const getAnswer = () => state.answers.get(state.current);

  const setAnswerForOption = ({ key, answer }) => {
    setTimeout(() => scrollTo(introArea.current), 1000);
    state.answers.set(state.current, { key, answer });
    setState({ ...state });
    setAnswers([...state.answers.entries()]);
  };

  return (
    <>
      <Intro ref={introArea}>
        {voting.ends && <div className="flex flex-col items-center">
          <span className="text-3xl">22,937</span>
          <span className="mb-4" style={{letterSpacing: 5}}>VOTES</span>
          <span className="text-purple-700 text-2xl px-5 py-2 rounded-full border border-purple-700">Voting Closed</span> <br/>
        </div>}
        {(!voting.ends && !state.emailVerified) && (
            <div>
              <P style={{ marginBottom: "1rem" }}>
                Enter your email to proceed.
              </P>
              <div>
                <Input
                  type="email"
                  placeholder="someone@domain.com"
                  large
                  required
                  danger
                  onKeyUp={({ target }) => {
                    setEmail(target.value);
                  }}
                />
                <Button
                  primary
                  className="sm:ml-2 mt-3 md:mt-0"
                  onClick={e => {
                    e.preventDefault();
                    if (isEmail(state.userEmail)) {
                      emailExist(state.userEmail)
                        .then(trace("New Email added"))
                        .then(async () => {
                          const userId = await addVoterEmail(state.userEmail);
                          setState({ ...state, emailVerified: true, userId });
                        })
                        .catch(err => {
                          log(err);
                          alert("Email already voted.");
                          // log(err.message);
                        });
                    }
                  }}
                >
                  Proceed
                </Button>
              </div>
            </div>
          )}
        {state.emailVerified && (
          <Stack large>
            <div>
              <P>
                <i>Pick a category to vote</i>
              </P>
              <br />
              <select
                name="category-picker"
                defaultValue={"disable"}
                onChange={({ target }) => {
                  setCurrent(log(+target.value, "the value"));
                  scrollTo(pollArea.current);
                }}
              >
                <option value="disable" disabled>
                  Select category
                </option>
                {polls.map((e, index) => (
                  <option key={index} value={index}>
                    {e.name}
                  </option>
                ))}
              </select>
            </div>
            {state.answers.size > 0 && (
              <Button outline onClick={() => doneVoting(state.userId)}>
                Done Voting
              </Button>
            )}
            <div className="flex flex-col items-center">
              <span>
                Email Address - <b>{state.userEmail}</b>
              </span>
              <span>
                No. of Votes - <b>{state.answers.size}</b>
              </span>
            </div>
          </Stack>
        )}
      </Intro>
      {state.emailVerified && (
        <section ref={pollArea} data-area="poll-area">
          <button
            data-role="scroll-up"
            onClick={() => {
              scrollTo(introArea.current);
            }}
          >
            <img src={scrollUp} alt="Scroll Up" />
            <span>GO BACK</span>
          </button>
          <Poll
            name={polls[state.current].name}
            options={polls[state.current].options}
            answer={getAnswer() || [-1]}
            onOptionPicked={setAnswerForOption}
          />
        </section>
      )}
    </>
  );
};

const scrollTo = el => {
  const cords = { x: 0, y: window.scrollY };
  anime({
    targets: cords,
    easing: "easeOutQuad",
    duration: 600,
    x: 0,
    y: el.offsetTop,
    update: () => window.scroll(cords.x, cords.y)
  });
};

export default App;
